import PropTypes from "prop-types"
import React, { useContext } from "react"
import { useQuery } from "react-query"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Divider from "src/components/Divider"
import Heading from "src/components/Heading"

import { reviewAgreement } from "src/api/Contracts/SignContract"

import MarinaNote from "../../MarinaNote"
import ReachOut from "../../ReachOut"
import { SignContractWizardContext } from "../../SignContractWizardContext"
import PurchaseSummary from "../AgreementOverview/PurchaseSummary"
import DetailsCard from "./DetailsCard"
import SignatureSection from "./SignatureSection"
import SubmitFailureNotice from "./SubmitFailureNotice"

const REVIEW_CONFIRMATION_TIMEOUT_MS = 45000

const ReviewAgreement = ({ isSubmitting }) => {
  const {
    contractChatUrl,
    dispatch,
    marina,
    quote,
    reachOut,
    state,
    steps,
    wizardCopy,
  } = useContext(SignContractWizardContext)

  const { review, reviewReady, paymentMethod, submitFailure } = state

  useQuery(
    ["reviewAgreement", quote.encodedId, paymentMethod],
    () =>
      reviewAgreement({
        quoteId: quote.encodedId,
        paymentMethodId: paymentMethod,
      }),
    {
      refetchInterval: 2000,
      enabled: !(reviewReady && review),
      onSuccess: (data) => {
        dispatch({ type: "REVIEW_LOADED", payload: data })

        if (
          data.status !== "signed" &&
          Date.now() - state.docusealFormSubmittedAt >
            REVIEW_CONFIRMATION_TIMEOUT_MS
        ) {
          dispatch({ type: "REVIEW_CONFIRMATION_FAILED" })
        }
      },
      onError: () => dispatch({ type: "REVIEW_CONFIRMATION_FAILED" }),
    }
  )

  if (!review) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="fa-solid fa-spinner-third fa-spin text-2xl text-gray-400" />
      </div>
    )
  }

  const {
    copy,
    surcharge,
    billingStart,
    pricingSummary,
    presentmentCurrency,
    signaturePath,
    status,
  } = review

  const extraLineItems = surcharge?.appliesToSelection
    ? [
        {
          type: "text_value",
          label: surcharge.rowLabel,
          value: surcharge.rowValue,
        },
      ]
    : []

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <Heading.HeroTitle>{copy.title}</Heading.HeroTitle>
        <p className="text-base font-semibold text-gray-700">{copy.subtitle}</p>
      </div>

      <div className="hidden lg:block [&>hr]:my-0">
        <Divider />
      </div>

      {surcharge?.appliesToSelection && (
        <AlertBanner type="warning">{surcharge.notice}</AlertBanner>
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="flex flex-col space-y-6 lg:col-span-2">
          <PurchaseSummary
            quote={quote}
            pricingSummary={pricingSummary}
            presentmentCurrency={presentmentCurrency}
            extraLineItems={extraLineItems}
            heading={copy.purchaseSummary}
          />

          <div>
            <h4 className="mb-2 mt-0 font-semibold text-gray-700">
              {copy.signature}
            </h4>
            <SignatureSection
              status={status}
              signaturePath={signaturePath}
              copy={copy}
            />
          </div>

          {submitFailure && (
            <SubmitFailureNotice
              failure={submitFailure}
              copy={{
                ...copy,
                backToAdditionalInformation:
                  wizardCopy?.backToAdditionalInformation,
                backToPaymentMethod: wizardCopy?.backToPaymentMethod,
              }}
              onBackToAdditionalInformation={() =>
                dispatch({
                  type: "SET_CURRENT_STEP",
                  payload:
                    steps.findIndex((step) => step.key === "additional") + 1,
                })
              }
              onBackToPayment={() =>
                dispatch({
                  type: "SET_CURRENT_STEP",
                  payload: steps.findIndex((step) => step.key === "pay") + 1,
                })
              }
            />
          )}

          {!submitFailure?.terminal && (
            <Button
              variant="primary"
              type="submit"
              fullWidth
              disabled={!reviewReady}
              isLoading={isSubmitting}
            >
              {copy.submit}
            </Button>
          )}

          <p className="flex items-center justify-center gap-2 text-gray-700">
            <i className="fa-solid fa-shield-check text-teal-600" />
            <span>{copy.secureNote}</span>
            <i className="fa-solid fa-lock" />
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <MarinaNote note={quote.notes} />
          <DetailsCard
            copy={copy}
            state={state}
            billingStart={billingStart}
            registrationRequired={quote.registrationRequired}
          />
          <ReachOut
            reachOut={reachOut}
            contractChatUrl={contractChatUrl}
            marinaId={marina.id}
            marinaName={marina.name}
            quoteId={quote.id}
          />
        </div>
      </div>
    </div>
  )
}

ReviewAgreement.propTypes = {
  isSubmitting: PropTypes.bool,
}

export default ReviewAgreement
